import { cardtraderAlignmentStatus } from 'lib/api'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useErrorHandler } from './useErrorHandler'

export type CardtraderAlignmentStatus =
  | 'fetching'
  | 'ready_for_review'
  | 'completed'
  | 'cancelled'
  | 'failed'

export interface CardtraderAlignmentStalePtProduct {
  _id: number
  mkmProductId: number
  condition: string
  idLanguage: number
  isFirstEd: boolean
  isSigned: boolean
  finishType: number | string
  countryEdition: number | string | null
  gradingCompany: string | null
  grade: string | null
}

export interface CardtraderAlignmentStaleItem {
  ctProductId: number
  ptProductId: number
  ptProduct: CardtraderAlignmentStalePtProduct
  quantity: number
  price: number | null
  currency: string | null
  comment: string
}

// Listings Cardtrader sent that nobody could place: a blueprint our catalogue
// does not know, a language or a condition we do not carry.
export interface CardtraderSkippedListing {
  blueprintId: number
  name: string
  quantity: number
  price: number
  currency: string
  condition: string
  comment: string
}

interface AlignmentStatusPayload {
  status: CardtraderAlignmentStatus
  jobId?: string
  // Null when the alignment covers the whole stock. Set when it is scoped to a
  // single game, which is another flow's business.
  idGame?: number | null
  fetchedAt?: string
  staleItems?: CardtraderAlignmentStaleItem[]
  skippedListings?: CardtraderSkippedListing[]
}

const POLL_INTERVAL_MS = 5000

export const useCardtraderAlignmentStatus = (enabled = true) => {
  const [status, setStatus] = useState<CardtraderAlignmentStatus | null>(null)
  const [fetchedAt, setFetchedAt] = useState<string | null>(null)
  const [staleItems, setStaleItems] = useState<CardtraderAlignmentStaleItem[]>([])
  const [skippedListings, setSkippedListings] = useState<CardtraderSkippedListing[]>([])
  const [idGame, setIdGame] = useState<number | null>(null)
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const inFlight = useRef(false)
  const { handle } = useErrorHandler()
  const handleRef = useRef(handle)
  useEffect(() => {
    handleRef.current = handle
  }, [handle])

  // The answer carries every stale listing with it, so on a large inventory it
  // takes seconds. A tick that fires while the previous one is still out would
  // queue a second copy of the same work behind it, and the ones after that
  // pile up on top: only the automatic poll gives way, an asked-for refresh
  // always goes.
  const fetchStatus = useCallback(async (skipWhileBusy = false) => {
    if (skipWhileBusy && inFlight.current) return

    inFlight.current = true
    try {
      const payload = (await cardtraderAlignmentStatus()) as AlignmentStatusPayload
      setStatus(payload.status)
      setIdGame(payload.idGame ?? null)
      setFetchedAt(payload.fetchedAt ?? null)
      setStaleItems(payload.staleItems ?? [])
      setSkippedListings(payload.skippedListings ?? [])
    } catch (error) {
      handleRef.current(error)
    } finally {
      inFlight.current = false
    }
  }, [])

  useEffect(() => {
    if (!enabled) return

    fetchStatus()
  }, [enabled, fetchStatus])

  useEffect(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }
    if (enabled && status === 'fetching') {
      intervalRef.current = setInterval(() => fetchStatus(true), POLL_INTERVAL_MS)
    }
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
    }
  }, [enabled, status, fetchStatus])

  return { status, idGame, fetchedAt, staleItems, skippedListings, refresh: fetchStatus }
}
